import { Box, Stack } from '@mui/material';
import { useAtomValue } from 'jotai';
import { Navigate, Outlet, RouteObject } from 'react-router-dom';

import { userInfoAtom } from '~atoms';
import { pagePathSegments, queryParameters } from '~constants';
import { UserRoleEnum } from '~enums';
import { ProtectedRoute, useAuthorize } from '~features/authentication';
import { OverviewStatisticsContainer } from '~features/statistics';
import { PageLayout } from '~layouts';
import PageHeader from '~layouts/page/headers/pageHeader';
import { BookingStatisticsPage, UserLicenseStatisticsPage } from '~pages';
import RevenueStatisticsPage from '~pages/statistics/revenueStatisticsPage';

const isHigherThanAcceptance =
	import.meta.env.VITE_APP_ENV === 'accept' || import.meta.env.VITE_APP_ENV === 'production';

const statisticsRoutes = (): RouteObject[] => [
	{
		path: pagePathSegments.Data,
		element: <TempNonSkopeiAdminRedirect />,
		children: [
			{
				path: '',
				element: (
					<Box>
						<Stack spacing={3} sx={{ px: 3, pt: 3 }}>
							<PageHeader i18nTitleKey='Data' enableOrganisationSelect />
							<OverviewStatisticsContainer />
						</Stack>
						<PageLayout
							pages={[
								{ i18nKey: 'ui.category.bookings', path: pagePathSegments.Bookings },
								{ i18nKey: 'nav.financialReports.revenue', path: pagePathSegments.Revenue },
								{
									i18nKey: 'nav.category.licenses',
									path: pagePathSegments.Verifications,
									roles: [UserRoleEnum.SuperAdmin],
								},
							]}
							persistedSearchParamKeys={[
								queryParameters.Periodicity,
								queryParameters.Date,
								queryParameters.Organisation,
							]}
						/>
					</Box>
				),
				children: [
					{
						index: true,
						element: <Navigate to={pagePathSegments.Bookings} />,
					},
					{
						path: pagePathSegments.Bookings,
						element: <BookingStatisticsPage />,
					},
					{
						path: pagePathSegments.Revenue,
						element: <RevenueStatisticsPage />,
					},
					{
						path: pagePathSegments.Verifications,
						element: <ProtectedRoute roles={[UserRoleEnum.SuperAdmin]} />,
						children: [
							{
								index: true,
								element: <UserLicenseStatisticsPage />,
							},
						],
					},
				],
			},
		],
	},
];

/**
 * Introduced to scope access to superadmins and skopei on production.
 * Should be remove ASAP (created: 2024-10-25) or only be used for experimental
 * features
 * @returns
 */
const TempNonSkopeiAdminRedirect = () => {
	const userInfo = useAtomValue(userInfoAtom);
	const { isSuperAdmin } = useAuthorize();

	const enableAccess =
		!isHigherThanAcceptance ||
		(isHigherThanAcceptance && (isSuperAdmin() || userInfo.organisation?.id == '2'));

	if (!enableAccess) {
		return <Navigate to={`/${pagePathSegments.Dashboard}`} />;
	}

	return <Outlet />;
};

export default statisticsRoutes;
